import { useState } from "react";
import { communityStats, hotThreads } from "@/mocks/homeContent";

const avatarTone: Record<string, string> = {
  primary: "bg-primary-100 text-primary-800",
  accent: "bg-accent-100 text-accent-900",
  secondary: "bg-secondary-200 text-secondary-900",
};

export default function CommunitySection() {
  const [liked, setLiked] = useState<boolean[]>(() => hotThreads.map(() => false));
  const [upvoted, setUpvoted] = useState(false);

  const like = (i: number) => setLiked((prev) => prev.map((v, idx) => (idx === i ? !v : v)));

  return (
    <section className="mx-auto w-full max-w-shell px-4 py-10 md:px-6 md:py-14">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-accent-700">COMMUNITY PULSE</p>
          <h2 className="mt-1 text-2xl font-black text-foreground-950 md:text-3xl">社区热帖 · 正在热议</h2>
          <p className="mt-2 text-sm text-foreground-500">网友按主题整理与复盘，就事论事、有理有据。</p>
        </div>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-add-line text-base" aria-hidden="true"></i>
          发起新话题
        </button>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
        {/* 左：热帖列表 */}
        <div className="lg:col-span-2">
          <div className="flex flex-col divide-y divide-background-200 rounded-xl border border-background-200 bg-background-50">
            {hotThreads.map((thread, idx) => (
              <div key={thread.title} className="flex gap-3 p-4 md:gap-4 md:p-5">
                <span
                  className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold ${avatarTone[thread.avatarTone]}`}
                >
                  {thread.author.slice(0, 1)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-400">
                    <span className="font-medium text-foreground-800">{thread.author}</span>
                    <span>{thread.time}</span>
                    <span className={`rounded-sm px-1.5 py-0.5 ${idx % 2 === 0 ? "bg-secondary-100 text-secondary-900" : "bg-primary-100 text-primary-800"}`}>
                      {thread.tag}
                    </span>
                    {thread.hot && (
                      <span className="inline-flex items-center gap-0.5 rounded-sm bg-accent-500 px-1.5 py-0.5 text-[10px] font-semibold text-foreground-950">
                        <i className="ri-fire-fill" aria-hidden="true"></i>
                        热
                      </span>
                    )}
                  </div>
                  <h3 className="mt-1.5 text-[15px] font-bold leading-snug text-foreground-950">{thread.title}</h3>
                  <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-foreground-500">{thread.excerpt}</p>
                  <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-foreground-400">
                    <button type="button" className="inline-flex items-center gap-1 hover:text-foreground-700">
                      <i className="ri-chat-3-line text-base" aria-hidden="true"></i>
                      {thread.replies} 跟帖
                    </button>
                    <button
                      type="button"
                      onClick={() => like(idx)}
                      aria-pressed={liked[idx]}
                      className={`inline-flex items-center gap-1 ${liked[idx] ? "text-primary-600" : "hover:text-foreground-700"}`}
                    >
                      <i className={`${liked[idx] ? "ri-thumb-up-fill" : "ri-thumb-up-line"} text-base`} aria-hidden="true"></i>
                      {liked[idx] ? Number(thread.likes.replace("k", "")) * 1 + "k" : thread.likes}
                    </button>
                    <button type="button" className="inline-flex items-center gap-1 hover:text-foreground-700">
                      <i className="ri-share-line text-base" aria-hidden="true"></i>
                      转发
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 右：社区数据 + 投票 */}
        <aside className="flex flex-col gap-5">
          <div className="grid grid-cols-3 gap-3">
            {communityStats.map((s) => (
              <div key={s.label} className="rounded-xl border border-background-200 bg-background-50 p-3 text-center">
                <p className="font-heading text-3xl leading-none text-primary-600">
                  {s.value}
                  <span className="ml-0.5 text-base text-primary-400">{s.unit}</span>
                </p>
                <p className="mt-1.5 text-[11px] text-foreground-500">{s.label}</p>
              </div>
            ))}
          </div>

          <div className="rounded-xl bg-background-900 p-5 text-background-100">
            <p className="text-sm font-bold text-background-50">本周社区小调查</p>
            <p className="mt-2 text-xs leading-relaxed text-background-200">你更关心哪一类争议的「处理结果」？</p>
            <div className="mt-3 space-y-2">
              {["售后是否真正退款", "官方是否正面回应", "背后运营是否被曝光"].map((opt, i) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setUpvoted(true)}
                  className={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-xs transition-colors ${
                    upvoted && i === 0 ? "border-accent-400 bg-accent-600 text-foreground-950" : "border-background-700 hover:border-background-500"
                  }`}
                >
                  <span>{opt}</span>
                  <span className="text-background-300">{upvoted && i === 0 ? "42%" : i === 1 ? "31%" : "27%"}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-background-200 bg-background-50 p-4">
            <div className="flex items-center gap-2 text-sm font-bold text-foreground-950">
              <i className="ri-secure-payment-line text-secondary-600" aria-hidden="true"></i>
              站内公告
            </div>
            <ul className="mt-3 space-y-2 text-xs leading-relaxed text-foreground-600">
              <li className="flex gap-2"><i className="ri-checkbox-circle-line mt-0.5 shrink-0 text-accent-600" aria-hidden="true"></i>收录前请先比对「收录与纠错」规范</li>
              <li className="flex gap-2"><i className="ri-checkbox-circle-line mt-0.5 shrink-0 text-accent-600" aria-hidden="true"></i>站内观点不代表平台立场，理性跟帖</li>
              <li className="flex gap-2"><i className="ri-checkbox-circle-line mt-0.5 shrink-0 text-accent-600" aria-hidden="true"></i>截图投稿请务必保留完整上下文</li>
            </ul>
          </div>
        </aside>
      </div>
    </section>
  );
}